/**
 * Salted hashing for the authenticated admin's punch ID (API-4551).
 *
 * The punch ID gates destructive Settings actions. Rather than keeping it in
 * readable React state/context (where it would show up in React DevTools or a
 * casual memory inspection), we hold only a salted SHA-256 digest and compare
 * digests. A fresh random salt per session means the stored digest differs each
 * time, so a known-plaintext digest (e.g. the hash of "1234") isn't recognizable.
 *
 * This is deliberately a low bar, not real secrecy: the account response still
 * carries `punch_id` over the network, and a short numeric PIN is brute-forceable
 * from any leaked digest. It only stops the value from being trivially read.
 */

function toHex(buffer: ArrayBuffer): string {
    return Array.from(new Uint8Array(buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

export function createSalt(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return toHex(bytes.buffer);
}

export async function hashPunchId(
    salt: string,
    punchId: string
): Promise<string> {
    const data = new TextEncoder().encode(`${salt}:${punchId}`);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return toHex(digest);
}
